import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const FileManager = () => {
  const [directories, setDirectories] = useState({});
  const [selectedBase, setSelectedBase] = useState('uploads');
  const [selectedDir, setSelectedDir] = useState(null);
  const [dirContents, setDirContents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [storageStats, setStorageStats] = useState({});
  const [showCreateDir, setShowCreateDir] = useState(false);
  const [newDirName, setNewDirName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [moveMode, setMoveMode] = useState(false);
  const [moveDestination, setMoveDestination] = useState(null);

  // Load directories on mount
  useEffect(() => {
    loadDirectories();
    loadStorageStats();
  }, []);

  const loadDirectories = async () => {
    try {
      setLoading(true);
      console.log('[FileManager] Loading directories from /api/admin/file-manager/directories');
      const response = await axios.get('/api/admin/file-manager/directories');
      console.log('[FileManager] Directories loaded:', response.data);
      setDirectories(response.data.directories);
      toast.success(`Loaded ${Object.keys(response.data.directories).length} base directories`);
    } catch (error) {
      console.error('[FileManager] Failed to load directories:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication required. Please login as admin.');
      } else {
        toast.error(`Failed to load directories: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStorageStats = async () => {
    try {
      const response = await axios.get('/api/admin/file-manager/storage-stats');
      setStorageStats(response.data.storage);
    } catch (error) {
      console.error('Failed to load storage stats:', error);
    }
  };

  const loadDirectoryContents = async (base, subdir) => {
    try {
      setLoading(true);
      const path = subdir ? `${base}/${subdir}` : base;
      const response = await axios.get(`/api/admin/file-manager/directory/${path}`);
      setDirContents(response.data);
      setSelectedDir({ base, subdir });
      setSelectedFiles([]);
    } catch (error) {
      console.error('Failed to load directory contents:', error);
      toast.error('Failed to load directory contents');
    } finally {
      setLoading(false);
    }
  };

  const createDirectory = async () => {
    if (!newDirName.trim()) {
      toast.error('Please enter a directory name');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/admin/file-manager/directory/create', {
        base: selectedBase,
        dir_name: newDirName.trim()
      });
      
      toast.success(`Directory '${newDirName}' created successfully`);
      setNewDirName('');
      setShowCreateDir(false);
      await loadDirectories();
    } catch (error) {
      console.error('Failed to create directory:', error);
      toast.error(error.response?.data?.detail || 'Failed to create directory');
    } finally {
      setLoading(false);
    }
  };

  const toggleFileSelection = (fileName) => {
    setSelectedFiles(prev => 
      prev.includes(fileName)
        ? prev.filter(f => f !== fileName)
        : [...prev, fileName]
    );
  };

  const startMoveMode = () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to move');
      return;
    }
    setMoveMode(true);
    toast('Select destination directory', { icon: '📂' });
  };

  const cancelMove = () => {
    setMoveMode(false);
    setMoveDestination(null);
  };

  const moveFiles = async (destBase, destSubdir) => {
    if (!selectedDir) {
      toast.error('No source directory selected');
      return;
    }

    const sourcePath = selectedDir.subdir 
      ? `/app/${selectedDir.base}/${selectedDir.subdir}`
      : `/app/${selectedDir.base}`;
    
    const destPath = destSubdir
      ? `/app/${destBase}/${destSubdir}`
      : `/app/${destBase}`;

    try {
      setLoading(true);
      const response = await axios.post('/api/admin/file-manager/files/move', {
        source_dir: sourcePath,
        dest_dir: destPath,
        file_names: selectedFiles
      });

      if (response.data.success) {
        toast.success(`Moved ${response.data.moved_count} file(s) successfully`);
        if (response.data.failed_count > 0) {
          toast.error(`Failed to move ${response.data.failed_count} file(s)`);
        }
      }

      // Reload current directory
      await loadDirectoryContents(selectedDir.base, selectedDir.subdir);
      setSelectedFiles([]);
      setMoveMode(false);
    } catch (error) {
      console.error('Failed to move files:', error);
      toast.error(error.response?.data?.detail || 'Failed to move files');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          📁 File Manager
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your files and directories safely
        </p>
      </div>

      {/* Storage Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {Object.entries(storageStats).map(([key, stats]) => (
          <div key={key} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">{key}</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.total_size_mb} MB
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {stats.total_files} files
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Directory Tree */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Directories</h2>
              <button
                onClick={() => setShowCreateDir(!showCreateDir)}
                className="mt-2 w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                + New Directory
              </button>
            </div>

            {showCreateDir && (
              <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  value={newDirName}
                  onChange={(e) => setNewDirName(e.target.value)}
                  placeholder="Directory name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg mb-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <div className="flex gap-2">
                  <button
                    onClick={createDirectory}
                    disabled={loading}
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateDir(false);
                      setNewDirName('');
                    }}
                    className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="p-2 max-h-96 overflow-y-auto">
              {loading && Object.keys(directories).length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>Loading directories...</p>
                </div>
              ) : Object.keys(directories).length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p className="mb-2">No directories found</p>
                  <button
                    onClick={loadDirectories}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                Object.entries(directories).map(([base, dirs]) => (
                  <div key={base} className="mb-2">
                    <button
                      onClick={() => {
                        setSelectedBase(base);
                        loadDirectoryContents(base, '');
                      }}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-medium text-gray-900 dark:text-white"
                    >
                      📂 {base}
                    </button>
                    
                    {dirs.length === 0 ? (
                      <div className="px-6 py-2 text-xs text-gray-500 dark:text-gray-400">
                        No subdirectories (click to view files)
                      </div>
                    ) : (
                      dirs.map((dir) => (
                        <button
                          key={dir.path}
                          onClick={() => {
                            if (moveMode) {
                              moveFiles(base, dir.relative_path);
                            } else {
                              loadDirectoryContents(base, dir.relative_path);
                            }
                          }}
                          className={`w-full text-left px-6 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-sm ${
                            moveMode ? 'cursor-pointer border-2 border-blue-500' : ''
                          }`}
                        >
                          <span className="text-gray-700 dark:text-gray-300">
                            📁 {dir.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            ({dir.file_count} files)
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Directory Contents */}
        <div className="lg:col-span-2">
          {moveMode && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-blue-800 dark:text-blue-200 font-medium">
                📦 Moving {selectedFiles.length} file(s). Click a directory to move files there.
              </p>
              <button
                onClick={cancelMove}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                Cancel Move
              </button>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {dirContents ? (
              <>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {dirContents.name || 'Root'}
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {dirContents.total_files} files, {dirContents.total_subdirs} subdirectories
                      </p>
                    </div>
                    {selectedFiles.length > 0 && !moveMode && (
                      <button
                        onClick={startMoveMode}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        Move Selected ({selectedFiles.length})
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  {/* Subdirectories */}
                  {dirContents.subdirectories.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subdirectories</h3>
                      <div className="space-y-1">
                        {dirContents.subdirectories.map((subdir) => (
                          <button
                            key={subdir.path}
                            onClick={() => {
                              if (moveMode) {
                                moveFiles(selectedDir.base, subdir.name);
                              } else {
                                loadDirectoryContents(
                                  selectedDir.base,
                                  selectedDir.subdir ? `${selectedDir.subdir}/${subdir.name}` : subdir.name
                                );
                              }
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${
                              moveMode ? 'border-2 border-blue-500' : ''
                            }`}
                          >
                            <span className="text-gray-900 dark:text-white">📁 {subdir.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              ({subdir.file_count} files)
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  {dirContents.files.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Files</h3>
                      <div className="space-y-1">
                        {dirContents.files.map((file) => (
                          <div
                            key={file.path}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFiles.includes(file.name)}
                              onChange={() => toggleFileSelection(file.name)}
                              className="w-4 h-4"
                              disabled={moveMode}
                            />
                            <div className="flex-1">
                              <p className="text-sm text-gray-900 dark:text-white">{file.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {file.size_kb} KB • {new Date(file.modified).toLocaleDateString()}
                              </p>
                            </div>
                            {file.extension && file.extension.match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                              <span className="text-green-600 dark:text-green-400 text-xs">🖼️ Image</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {dirContents.files.length === 0 && dirContents.subdirectories.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <p>📂 Empty directory</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <p>Select a directory to view its contents</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">ℹ️ Safe File Management</h3>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <li>• Only safe directories are accessible (uploads, backups)</li>
          <li>• System and application directories are protected</li>
          <li>• You can create new directories and move files between them</li>
          <li>• Empty directories can be deleted for cleanup</li>
        </ul>
      </div>
    </div>
  );
};

export default FileManager;
